import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buildFilterOptions, matchesColumnFilters, sortRows } from '../utils/columns';
import { formatDayRange } from '../utils/format';
import { matchesSearch, matchesTab, summarize, TAB_KEYS } from '../utils/registrations';
import { reportStorageKey } from './storageKeys';
import { hasStoredValue, usePersistentState } from './usePersistentState';

/*
 * Browser-side filtering, sorting, grouping, paging and selection over the report rows.
 * Ported from the reference without its local edits (status changes, notes, reminders, offer
 * spot): the report is read-only.
 *
 * columns = { order, meta } from utils/columns buildColumns. dataKey = campCode:year.
 *
 * Tab, search, filters, page and the open row are kept for this browser tab (sessionStorage), so
 * coming back to the page lands where the director left. Sort, grouping and page size are kept
 * across sessions (localStorage), as in the reference.
 */

const STATUS_GROUPS = ['Active', 'Pending', 'Refunded', 'Cancelled', 'Cancelled & refunded'];

// "All" rows per page still pages at 100. Every drawn row costs style, layout and a compositing
// layer per pinned cell, so a few hundred rows at 46 columns freeze the page for seconds on each
// change (profiled: grouping at 500 rows took a 3.8 s frame). Grouped views use the chosen page
// size too; a group that runs over a page shows as continued.
export const ALL_PAGE_CAP = 100;
const PAGE_SIZES = [10, 25, 50, 100, 'all'];

const groupKeyOf = (r, groupBy) => {
  if (groupBy === 'program') return r.programId;
  if (groupBy === 'stay') return r.stay;
  if (groupBy === 'status') return r.status;
  return '';
};

export function useRegistrationsTable({ rows, programs, columns, campCode, dataKey }) {
  const { order: columnOrder, meta } = columns;
  const sessionKey = name => reportStorageKey(campCode, `session.${name}`);

  // Read once: whether this tab already has filters for the camp (the page then skips restoring
  // the saved view's filters over them).
  const [fromSession] = useState(() => hasStoredValue(sessionKey('tab'), 'session'));
  const [tab, setTab] = usePersistentState(sessionKey('tab'), 'all', 'session');
  const [search, setSearch] = usePersistentState(sessionKey('search'), '', 'session');
  const [columnFilters, setColumnFilters] = usePersistentState(sessionKey('columnFilters'), {}, 'session');
  const [textFilters, setTextFilters] = usePersistentState(sessionKey('textFilters'), {}, 'session');
  const [sort, setSort] = usePersistentState(reportStorageKey(campCode, 'sort'), null);
  const [groupBy, setGroupBy] = usePersistentState(reportStorageKey(campCode, 'groupBy'), 'none');
  const [storedPageSize, setPageSize] = usePersistentState(reportStorageKey(campCode, 'pageSize'), 10);
  const pageSize = PAGE_SIZES.includes(storedPageSize) ? storedPageSize : 10;
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [page, setPage] = usePersistentState(sessionKey('page'), 1, 'session');
  const [activeId, setActiveId] = usePersistentState(sessionKey('activeId'), null, 'session');

  // Another camp or season clears the selection and the open drawer. A background refresh of the
  // same data set keeps both: rows keep their ids.
  const lastDataKey = useRef(dataKey);
  useEffect(() => {
    if (lastDataKey.current === dataKey) return;
    lastDataKey.current = dataKey;
    setSelectedIds(new Set());
    setActiveId(null);
  }, [dataKey, setActiveId]);

  const tabCounts = useMemo(
    () => Object.fromEntries(TAB_KEYS.map(k => [k, rows.filter(r => matchesTab(r, k)).length])),
    [rows]
  );

  const tabRows = useMemo(() => rows.filter(r => matchesTab(r, tab)), [rows, tab]);
  const filteredRows = useMemo(
    () => tabRows.filter(r => matchesSearch(r, search) && matchesColumnFilters(r, columnFilters, textFilters, meta)),
    [tabRows, search, columnFilters, textFilters, meta]
  );
  const sortedRows = useMemo(() => (sort && meta[sort.key] ? sortRows(filteredRows, sort, meta) : filteredRows), [filteredRows, sort, meta]);

  const groups = useMemo(() => {
    if (groupBy === 'none') return [];
    let keys;
    if (groupBy === 'program') keys = programs.map(p => p.programId);
    else if (groupBy === 'status') keys = STATUS_GROUPS;
    else keys = ['Overnight', 'Commuter'];
    const programsById = new Map(programs.map(p => [p.programId, p]));
    return keys
      .map(key => {
        const groupRows = sortedRows.filter(r => groupKeyOf(r, groupBy) === key);
        const program = groupBy === 'program' ? programsById.get(key) : null;
        return {
          key,
          label: program ? `${program.programName}${program.isDeleted ? ' (deleted)' : ''}` : key,
          sublabel: program ? formatDayRange(program.startDate, program.endDate) : undefined,
          rows: groupRows,
          summary: summarize(groupRows),
        };
      })
      .filter(g => g.rows.length > 0);
  }, [groupBy, sortedRows, programs]);

  const grouped = groupBy !== 'none';
  const orderedRows = useMemo(() => (grouped ? groups.flatMap(g => g.rows) : sortedRows), [grouped, groups, sortedRows]);
  const effectiveSize = pageSize === 'all' ? ALL_PAGE_CAP : pageSize;
  const pageCount = Math.max(1, Math.ceil(orderedRows.length / effectiveSize));
  const currentPage = Math.min(page, pageCount);
  const pagedRows = useMemo(
    () => orderedRows.slice((currentPage - 1) * effectiveSize, currentPage * effectiveSize),
    [orderedRows, currentPage, effectiveSize]
  );

  // The groups on this page. Header count and subtotals stay those of the whole group; a group that
  // started on an earlier page is marked as continued.
  const pageGroups = useMemo(() => {
    if (!grouped) return [];
    const onPage = new Set(pagedRows.map(r => r.id));
    return groups
      .map(g => {
        const rowsOnPage = g.rows.filter(r => onPage.has(r.id));
        return { ...g, total: g.rows.length, continued: rowsOnPage.length > 0 && rowsOnPage[0] !== g.rows[0], rows: rowsOnPage };
      })
      .filter(g => g.rows.length > 0);
  }, [grouped, groups, pagedRows]);

  // Back to page 1 when the result set changes, but not on mount: the page came from this tab's session.
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    setPage(1);
  }, [tab, search, columnFilters, textFilters, sort, groupBy, pageSize, setPage]);

  const filterOptions = useMemo(
    () => Object.fromEntries(columnOrder.map(k => [k, buildFilterOptions(tabRows, k, meta)])),
    [tabRows, columnOrder, meta]
  );

  const activeFilterCount =
    Object.values(columnFilters).filter(v => v && v.length).length +
    Object.values(textFilters).filter(v => v && v.trim()).length;
  const isFiltered = tab !== 'all' || search.trim().length > 0 || activeFilterCount > 0;

  const selectedRows = useMemo(() => rows.filter(r => selectedIds.has(r.id)), [rows, selectedIds]);
  const scopeKind = selectedRows.length ? 'selected' : isFiltered ? 'filtered' : 'all';
  const scopeRows = scopeKind === 'selected' ? selectedRows : scopeKind === 'filtered' ? sortedRows : rows;
  const summary = useMemo(() => summarize(scopeRows), [scopeRows]);

  const toggleRow = useCallback(id => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const allFilteredSelected = sortedRows.length > 0 && sortedRows.every(r => selectedIds.has(r.id));
  const someFilteredSelected = sortedRows.some(r => selectedIds.has(r.id));

  const toggleAll = useCallback(() => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (sortedRows.every(r => next.has(r.id))) sortedRows.forEach(r => next.delete(r.id));
      else sortedRows.forEach(r => next.add(r.id));
      return next;
    });
  }, [sortedRows]);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const setColumnFilter = useCallback((key, values) => {
    setColumnFilters(prev => {
      const next = { ...prev };
      if (values.length) next[key] = values;
      else delete next[key];
      return next;
    });
  }, [setColumnFilters]);

  const setTextFilter = useCallback((key, value) => {
    setTextFilters(prev => {
      const next = { ...prev };
      if (value.trim()) next[key] = value.trim();
      else delete next[key];
      return next;
    });
  }, [setTextFilters]);

  const clearAllFilters = useCallback(() => {
    setColumnFilters({});
    setTextFilters({});
    setSearch('');
    setTab('all');
  }, [setColumnFilters, setTextFilters, setSearch, setTab]);

  const applyFilterState = useCallback(
    state => {
      setTab(TAB_KEYS.includes(state.tab) ? state.tab : 'all');
      setSearch(state.search);
      setColumnFilters(state.columnFilters);
      setTextFilters(state.textFilters);
      setGroupBy(state.groupBy);
    },
    [setGroupBy, setTab, setSearch, setColumnFilters, setTextFilters]
  );

  const applyViewState = useCallback(
    state => {
      applyFilterState(state);
      setSort(state.sort);
      setCollapsedGroups(new Set());
    },
    [applyFilterState, setSort]
  );

  const toggleGroup = useCallback(key => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const showMissingDocuments = useCallback(() => {
    setColumnFilter('documents', ['Missing documents']);
    clearSelection();
  }, [setColumnFilter, clearSelection]);

  const activeRegistration = rows.find(r => r.id === activeId) ?? null;
  const activeIndex = activeId ? orderedRows.findIndex(r => r.id === activeId) : -1;

  const openAt = useCallback(
    index => {
      const target = orderedRows[index];
      if (!target) return;
      setActiveId(target.id);
      setPage(Math.floor(index / effectiveSize) + 1);
      if (grouped) {
        const key = groupKeyOf(target, groupBy);
        setCollapsedGroups(prev => {
          if (!prev.has(key)) return prev;
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
    [orderedRows, grouped, groupBy, effectiveSize, setActiveId, setPage]
  );

  const openNext = useCallback(() => activeIndex >= 0 && openAt(activeIndex + 1), [activeIndex, openAt]);
  const openPrev = useCallback(() => activeIndex > 0 && openAt(activeIndex - 1), [activeIndex, openAt]);

  return {
    rows, tab, setTab, tabCounts, search, setSearch,
    columnFilters, textFilters, setColumnFilter, setTextFilter, clearAllFilters, activeFilterCount, isFiltered,
    applyFilterState, applyViewState,
    sort, setSort, groupBy, setGroupBy, groups, pageGroups, collapsedGroups, toggleGroup,
    sortedRows, orderedRows, pagedRows, currentPage, pageCount, setPage, pageSize, pageLength: effectiveSize, setPageSize, filterOptions,
    selectedIds, selectedRows, toggleRow, toggleAll, clearSelection, allFilteredSelected, someFilteredSelected,
    activeRegistration, activeIndex, setActiveId, openNext, openPrev, showMissingDocuments,
    summary, scopeKind, fromSession,
  };
}
